/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';

type HookProps = {
  api: vp.Api;
};

type ReturnTuple = [PartnerInvoicesResponse | undefined, (params: PartnerInvoicesRequestParams) => void, () => void];

export type PartnerInvoicesRequestParams = {
  fromDate: string;
  toDate: string;
  currency: vp.SupportedCurrencies | '';
};

export type PartnerInvoicesResponse = {
  success: boolean;
  errorMessage: string;
  partnerInvoices: vp.PartnerInvoice[];
};

function useFetchPartnerInvoices({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<PartnerInvoicesRequestParams>();
  const [response, setResponse] = useState<PartnerInvoicesResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  useEffect(() => {
    const sendRequest = async (requestParams: PartnerInvoicesRequestParams) => {
      setDataFetchingInfo(true, 'Loading Data', 'fullScreen');

      const { Success, ErrorMessage, PartnerInvoices } = await api.fetchPartnerInvoices(
        buildParams(requestParams),
        source,
      );

      const res = {
        success: Success,
        errorMessage: ErrorMessage,
        partnerInvoices: Object.values(PartnerInvoices || {}),
      };

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams?: PartnerInvoicesRequestParams) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
  ];

  function buildParams(requestParams: PartnerInvoicesRequestParams): vp.InvoicesRequest {
    return {
      FromDate: requestParams.fromDate,
      ToDate: requestParams.toDate,
      Currency: requestParams.currency,
    };
  }
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchPartnerInvoices);
